import { Button, Col, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { useEffect } from "react";

import { api_without_cancellation } from "../../../api/api";
import ContactPanelForEditPopups from "../../TreatmentPage/components/ContactPanelForEditPopups";
import SelectStateModal from "../../TreatmentPage/modals/state-modal/SelectStateModal";
import { getCaseId, getClientId, inputCurrencyFormat2 } from "../../../Utils/helper";

const capitalize = (v) =>
  typeof v === "string" && v.length > 0 ? v.charAt(0).toUpperCase() + v.slice(1) : v;

const registerCapitalized = (register, setValue, name, options = {}) => ({
  ...register(name, {
    ...options,
    onChange: (e) => {
      const v = e.target.value;
      if (v.length > 0) {
        setValue(name, capitalize(v), { shouldValidate: false, shouldDirty: true });
      }
      options.onChange?.(e);
    },
  }),
});

const CostRequestModalExpert = ({
  show,
  onClose,
  first_name,
  middle_name,
  last_name,
  name,
  address1,
  address2,
  city,
  state,
  zip_code,
  phone_number,
  fax_number,
  ext = "",
  email,
  page_id,
  amount,
  cost_name,
  source_model,
  source_key,
  source_id,
  expert_agency,
  category_suffix = "",
}) => {
  const { register, watch, setValue, handleSubmit, reset } = useForm();
  const [loading, setLoading] = useState(false);
  const [payeeType, setPayeeType] = useState("expert");

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await api_without_cancellation.post("/api/cost-requests/", {
        first_name: data.first_name,
        middle_name: data.middle_name,
        last_name: data.last_name,
        name: data.name,
        address1: data.address1,
        address2: data.address2,
        city: data.city,
        state: data.state,
        zip_code: data.zip,
        phone_number: data.phone,
        fax_number: data.fax,
        ext: data.extension,
        email: data.email,
        page_id: data.page,
        amount: data.amount,
        cost_name: cost_name,
        invoice_number: data.invoice_number,
        memo: data.memo,
        source_model: source_model,
        source_key: payeeType === "expert_agency" ? "expert_firm" : source_key,
        source_id: source_id,
        case_id: getCaseId(),
        client_id: getClientId(),
        paid_by: "Pending",
      });
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const handleStateShow = () => setShowStateDropdown((s) => !s);

  register("state");
  // Watch values needed by ContactPanel + conditional rendering
  const payeeState = watch("state");
  const payeePhoneValue = watch("phone");
  const payeeFaxValue = watch("fax");

  const formatPhone = (raw) => {
    const digits = (raw ?? "").replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const handleHomePhoneInputChange = (e) => setValue("phone", formatPhone(e.target.value));
  const handleHomeFaxInputChange = (e) => setValue("fax", formatPhone(e.target.value));

  const handlePayeeTypeChange = (type) => {
    const isAgency = type === "expert_agency";
    const payee = isAgency
      ? {
          first_name: "",
          middle_name: "",
          last_name: "",
          name: expert_agency?.name ?? "",
          address1: expert_agency?.address1 ?? "",
          address2: expert_agency?.address2 ?? "",
          city: expert_agency?.city ?? "",
          state: expert_agency?.state ?? "",
          zip: expert_agency?.zip ?? "",
          phone: expert_agency?.phone_number ? formatPhone(expert_agency.phone_number) : "",
          extension: expert_agency?.phone_ext ?? "",
          fax: expert_agency?.fax ? formatPhone(expert_agency.fax) : "",
          email: expert_agency?.email ?? "",
          website: expert_agency?.website ?? "",
        }
      : {
          first_name: first_name ?? "",
          middle_name: middle_name ?? "",
          last_name: last_name ?? "",
          name: name ?? "",
          address1: address1 ?? "",
          address2: address2 ?? "",
          city: city ?? "",
          state: state ?? "",
          zip: zip_code ?? "",
          phone: phone_number ? formatPhone(phone_number) : "",
          extension: ext ?? "",
          fax: fax_number ? formatPhone(fax_number) : "",
          email: email ?? "",
          website: "",
        };

    setPayeeType(type);
    Object.entries(payee).forEach(([field, value]) => {
      setValue(field, value, { shouldDirty: false });
    });
  };

  const [statesAbrs, setStatesAbrs] = useState([]);
  const [pages, setPages] = useState([]);
  // const [showPageDropdown, setShowPageDropdown] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);

  const fetchStatesData = async () => {
    try {
      const response = await api_without_cancellation.get("/api/states/");
      if (response.status === 200) {
        setStatesAbrs(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPages = async () => {
    try {
      const response = await api_without_cancellation.get("/api/getPages/");
      setPages(response.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchStatesData();
    fetchPages();
  }, []);

  const handleStateClose = () => setShowStateDropdown(false);

  useEffect(() => {
    if (!show) return;
    setPayeeType("expert");
    reset({
      first_name: first_name ?? "",
      middle_name: middle_name ?? "",
      last_name: last_name ?? "",
      name: name ?? "",
      address1: address1 ?? "",
      address2: address2 ?? "",
      city: city ?? "",
      state: state ?? "",
      zip: zip_code ?? "",
      phone: phone_number ? formatPhone(phone_number) : "",
      extension: ext ?? "",
      fax: fax_number ? formatPhone(fax_number) : "",
      email: email ?? "",
      website: "",
      page: page_id ?? null,
      amount: amount !== null && amount !== undefined ? String(amount) : "",
    });
  }, [show]);

  useEffect(() => {
    if (!page_id || pages.length === 0) return;
    const match = pages.find((p) => p.id === page_id);
    if (match) setSelectedPage(match);
  }, [pages, page_id]);
  const [amountFocused, setAmountFocused] = useState(false);
  const [previousAmount, setPreviousAmount] = useState("");

  const amountValue = watch("amount");
  return (
    <>
      <Modal show={show} onHide={onClose} dialogClassName="modal-dialog-centered modal-800p">
        <div
          className="text-center height-25 p-0 bg-primary modal-header"
          style={{ border: "none" }}
        >
          <div
            className="mx-auto height-25 text-white d-flex align-items-center"
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            Cost Request
          </div>
        </div>
        <Modal.Body style={{ padding: "5px 5px 0px 5px" }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div
              className="w-100"
              style={{
                minHeight: "250px",
              }}
            >
              <Row className="mx-0 d-flex m-t-5">
                <ContactPanelForEditPopups
                  pageName="Treatment"
                  // name={`${watch("first_name") || ""}  ${watch("last_name") || ""}`}
                  name={`${watch("name") || ""}`}
                  // dynamic_label={defendantType !== 1 ? "Entity Name" : ""}
                  panel_name={"Payee Information"}
                  phone_number={watch("phone")}
                  fax_number={watch("fax")}
                  email={watch("email")}
                  address1={watch("address1")}
                  address2={watch("address2")}
                  city={watch("city")}
                  state={watch("state")}
                  zip_code={watch("zip")}
                  ext={watch("extension")}
                  websiteURL={watch("website")}
                  buttonData={[]}
                  genrate_doc_address={"Payee Address"}
                />

                <Col md="auto" className="p-0 d-flex-1">
                  <div
                    className="d-flex align-items-center justify-content-center m-b-5"
                    style={{ gap: "15px" }}
                  >
                    <label
                      className="d-flex align-items-center cursor-pointer mb-0"
                      style={{ gap: "5px" }}
                    >
                      <input
                        type="radio"
                        name="expert-cost-payee"
                        value="expert"
                        checked={payeeType === "expert"}
                        onChange={() => handlePayeeTypeChange("expert")}
                      />
                      Expert
                    </label>
                    <label
                      className="d-flex align-items-center cursor-pointer mb-0"
                      style={{ gap: "5px" }}
                    >
                      <input
                        type="radio"
                        name="expert-cost-payee"
                        value="expert_agency"
                        checked={payeeType === "expert_agency"}
                        onChange={() => handlePayeeTypeChange("expert_agency")}
                      />
                      Expert Agency
                    </label>
                  </div>
                  <Row className="align-items-center custom-margin-bottom mx-0">
                    <Col md={12} className="p-0">
                      <div className="row mx-0 align-items-center custom-margin-bottom">
                        <div className="d-flex-1">
                          <input
                            type="text"
                            placeholder="Name"
                            className="form-control rounded-0 height-25"
                            // {...register("name")}
                            {...registerCapitalized(register, setValue, "name")}
                          />
                        </div>
                        {/* <div className="d-flex-1 p-r-5">
                          <input
                            type="text"
                            placeholder="First Name"
                            className="form-control rounded-0 height-25"
                            // {...register("first_name")}
                            {...registerCapitalized(register, setValue, "first_name")}
                          />
                        </div>
                        <div className="d-flex-1 p-r-5">
                          <input
                            type="text"
                            placeholder="Middle Name"
                            className="form-control rounded-0 height-25"
                            // {...register("middle_name")}
                            {...registerCapitalized(register, setValue, "middle_name")}
                          />
                        </div>
                        <div className="d-flex-1">
                          <input
                            type="text"
                            placeholder="Last Name"
                            className="form-control rounded-0 height-25"
                            // {...register("last_name")}
                            {...registerCapitalized(register, setValue, "last_name")}
                          />
                        </div> */}
                      </div>

                      <div className="row mx-0 align-items-center custom-margin-bottom">
                        <div className="d-flex-1 p-r-5">
                          <input
                            type="text"
                            placeholder="Address 1"
                            className="form-control rounded-0 height-25"
                            // {...register("address1")}
                            {...registerCapitalized(register, setValue, "address1")}
                          />
                        </div>
                        <div className="d-flex-1">
                          <input
                            type="text"
                            placeholder="Address 2"
                            className="form-control rounded-0 height-25"
                            // {...register("address2")}
                            {...registerCapitalized(register, setValue, "address2")}
                          />
                        </div>
                      </div>

                      <div className="row mx-0 align-items-center custom-margin-bottom">
                        <div className="d-flex-1 p-l-0 p-r-5">
                          <input
                            type="text"
                            placeholder="City"
                            className="form-control rounded-0 height-25"
                            // {...register("city")}
                            {...registerCapitalized(register, setValue, "city")}
                          />
                        </div>
                        <div
                          className="d-flex-1 p-l-0 position-relative height-25 p-r-5 custom-select-new-provider"
                          style={{ maxWidth: "90px" }}
                        >
                          <div
                            className="dropdown-button rounded-0 p-0 p-l-5 form-control height-25 d-flex align-items-center"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleStateShow();
                              }
                            }}
                            onClick={handleStateShow}
                          >
                            <span id="selectedOption">
                              {payeeState ? (
                                <div className="d-flex align-items-center">
                                  <svg
                                    style={{
                                      width: "15px",
                                      height: "15px",
                                      fill: "var(--primary-80)",
                                      color: "var(--primary-80)",
                                      stroke: "var(--primary-80)",
                                    }}
                                    className={`icon icon-state-${payeeState}`}
                                  >
                                    <use xlinkHref={`#icon-state-${payeeState}`} />
                                  </svg>
                                  {payeeState}
                                </div>
                              ) : (
                                "State"
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="d-flex-1 pe-0" style={{ maxWidth: "90px" }}>
                          <input
                            type="text"
                            placeholder="Zip"
                            className="form-control rounded-0 height-25"
                            {...register("zip")}
                          />
                        </div>
                      </div>

                      <div className="row mx-0 align-items-center custom-margin-bottom">
                        <div className="d-flex-1 p-r-5 p-l-0">
                          <input
                            type="text"
                            placeholder="(###) ###-####"
                            className="form-control rounded-0 height-25"
                            {...register("phone")}
                            value={payeePhoneValue || ""}
                            onChange={handleHomePhoneInputChange}
                          />
                        </div>
                        <div className="d-flex-1" style={{ maxWidth: "90px" }}>
                          <input
                            type="text"
                            placeholder="Ext"
                            className="form-control rounded-0 height-25"
                            {...register("extension", {
                              onChange: (e) => {
                                const sanitized = e.target.value.replace(/[^0-9]/g, "");
                                setValue("extension", sanitized);
                              },
                            })}
                          />
                        </div>
                        <div className="d-flex-1 ps-1 pe-0">
                          <input
                            type="text"
                            placeholder="(###) ###-####"
                            className="form-control rounded-0 height-25"
                            {...register("fax")}
                            value={payeeFaxValue || ""}
                            onChange={handleHomeFaxInputChange}
                          />
                        </div>
                      </div>

                      <div className="row mx-0 align-items-center custom-margin-bottom">
                        <div className="d-flex-1">
                          <input
                            type="email"
                            placeholder="Email"
                            className="form-control rounded-0 height-25"
                            {...register("email")}
                          />
                        </div>
                      </div>

                      <div className="row mx-0 align-items-center custom-margin-bottom">
                        <div className="d-flex-1">
                          <input
                            type="text"
                            placeholder="Website"
                            className="form-control rounded-0 height-25"
                            {...register("website")}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <div className="d-flex height-25 m-t-5">
                <div className="col-4 text-end p-r-5">Category:</div>
                <div className="col-8 p-l-5 p-r-5">
                  <div className="d-flex align-items-center">
                    <img
                      src={selectedPage?.page_icon}
                      style={{ width: "15px", height: "15px", marginRight: "5px" }}
                      alt=""
                    />
                    {selectedPage?.name}
                    {category_suffix && (
                      <span style={{ marginLeft: "5px" }}> - {category_suffix}</span>
                    )}
                  </div>
                </div>
                {/* <div
                  className="col-10 d-flex-1 p-0 position-relative height-25 custom-select-new-provider"
                  style={{ margin: "0 0 5px 0" }}
                >
                  <div
                    className="dropdown-button rounded-0 p-0 p-l-5 form-control height-25 d-flex align-items-center"
                    tabIndex={0}
                    // onClick={() => setShowPageDropdown((s) => !s)}
                    // onKeyDown={(e) => {
                    //   if (e.key === "Enter" || e.key === " ") {
                    //     e.preventDefault();
                    //     setShowPageDropdown((s) => !s);
                    //   }
                    // }}
                  >
                    <span>
                      {selectedPage ? (
                        <div className="d-flex align-items-center">
                          <img
                            src={selectedPage.page_icon}
                            style={{ width: "15px", height: "15px" }}
                            alt=""
                          />
                          {selectedPage.name}
                        </div>
                      ) : (
                        "Select Category"
                      )}
                    </span>
                  </div>
                  {showPageDropdown && (
                    <div
                      className="dropdown-menu p-0 mt-0 show"
                      style={{
                        width: "100%",
                        border: "1px solid #ced4da",
                        borderRadius: "0px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        scrollbarWidth: "none",
                        zIndex: 1060,
                      }}
                    >
                      {pages?.length > 0 ? (
                        pages.map((page, index) => (
                          <div
                            key={page.id}
                            className="dropdown-option d-flex align-items-center height-25"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setSelectedPage(page);
                              setValue("page", page.id);
                              setShowPageDropdown(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedPage(page);
                                setValue("page", page.id);
                                setShowPageDropdown(false);
                              }
                            }}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "var(--primary-5)" : "var(--primary-10)",
                              cursor: "pointer",
                              fontSize: "14px",
                              paddingLeft: "5px",
                              gap: "6px",
                            }}
                          >
                            <img
                              src={page.page_icon}
                              style={{ width: "15px", height: "15px" }}
                              alt=""
                            />
                            {page.name}
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-option d-flex align-items-center height-25 p-l-5">
                          No pages found
                        </div>
                      )}
                    </div>
                  )}
                </div> */}
              </div>
              <div className="d-flex height-25 m-t-5" style={{ margin: "0 0 5px 0" }}>
                <div className="col-4 text-end p-r-5">{cost_name || "Amount"}:</div>
                <div className="col-8 d-flex-1 p-0 d-flex align-items-center position-relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={amountFocused ? "" : "0.00"}
                    className="form-control rounded-0 height-25"
                    style={{ paddingLeft: "18px" }}
                    value={
                      amountFocused
                        ? `$ ${amountValue ?? ""}`
                        : amountValue !== "" && amountValue !== null
                          ? inputCurrencyFormat2(amountValue)
                          : ""
                    }
                    {...register("amount", {
                      onChange: (e) => {
                        let value = e.target.value.replace(/[^0-9.]/g, "");

                        if ((value.match(/\./g) || []).length > 1) {
                          value = value.substring(0, value.lastIndexOf("."));
                        }

                        if (value.includes(".")) {
                          const parts = value.split(".");
                          if (parts[1].length > 4) {
                            parts[1] = parts[1].substring(0, 4);
                          }
                          value = parts.join(".");
                        }

                        setValue("amount", value);
                      },
                    })}
                    onFocus={() => {
                      setPreviousAmount(amountValue);
                      setValue("amount", "");
                      setAmountFocused(true);
                    }}
                    onBlur={() => {
                      setAmountFocused(false);

                      if (!amountValue) {
                        setValue("amount", previousAmount);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="d-flex height-25 m-t-5" style={{ margin: "0 0 5px 0" }}>
                <div className="col-4 text-end p-r-5">Invoice Number:</div>
                <div className="col-8 d-flex-1 p-0 d-flex align-items-center position-relative">
                  <input
                    type="text"
                    placeholder="Invoice Number"
                    className="form-control rounded-0 height-25"
                    style={{ paddingLeft: "18px" }}
                    {...register("invoice_number")}
                  />
                </div>
              </div>
              <div className="d-flex height-25 m-t-5" style={{ margin: "0 0 5px 0" }}>
                <div className="col-4 text-end p-r-5">Memo:</div>
                <div className="col-8 d-flex-1 p-0 d-flex align-items-center position-relative">
                  <input
                    type="text"
                    placeholder="Memo"
                    className="form-control rounded-0 height-25"
                    style={{ paddingLeft: "18px" }}
                    {...register("memo")}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex mt-auto w-100 justify-content-between p-b-5">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                className="height-25"
                style={{ padding: "0px 12px" }}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                type="submit"
                className="m-l-5 bg-success height-25"
                style={{ padding: "0px 12px" }}
                disabled={loading}
              >
                {loading ? "Request Cost and Close" : "Request Cost and Close"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      {showStateDropdown && (
        <SelectStateModal
          show={showStateDropdown}
          handleClose={handleStateClose}
          onChange={(s) => setValue("state", s?.StateAbr)}
          statesData={statesAbrs}
        />
      )}
    </>
  );
};

export default CostRequestModalExpert;
